import { useEffect } from "react";
import {
  Activitystream,
  useActivitystreams,
} from "../../../api/activitystreams";
import ReactSelect from "react-select";
import { filter, find, get, groupBy, map, orderBy, toLower } from "lodash";
import Label from "../../shared/form/Label";
import { Control, Controller, useFormContext } from "react-hook-form";
import classNames from "classnames";
import Hint from "../../shared/form/fields/Hint.tsx";

type StreamTargeting = {
  currentStream?: { slug: string; name: string } | null;
  control?: Control<any>;
  filterCan?: string;
  hint?: string;
  // The host pre-determines the targeting (share flows) — don't derive a
  // default from the current stream.
  hasPresetTargeting?: boolean;
};

export default function StreamTargeting({
  currentStream,
  control,
  filterCan = "create",
  hint,
  hasPresetTargeting = false,
}: StreamTargeting) {
  const {
    setValue,
    formState: { errors },
  } = useFormContext();
  const { data: activitystreams, isLoading } = useActivitystreams();

  const stream = find(
    activitystreams,
    (stream) => stream.slug === currentStream?.slug,
  );

  const isWritable = (stream: Activitystream) =>
    !!get(stream, ["can", filterCan]) && !stream.archived;

  // Meta streams (the virtual "all" stream) report can.create but are never
  // valid post targets — exclude them once here.
  const writeableStreams: Activitystream[] = filter(
    activitystreams,
    (stream: Activitystream) => !stream.meta && isWritable(stream),
  );

  // Network-level streams are the ones without a group_slug.
  const firstWritableNetworkStream = find(
    writeableStreams,
    (stream) => !stream.group_slug,
  );

  // The default target is a pure derivation of the current stream: the stream
  // itself when writable; for the virtual "all" stream the first writable
  // network stream; otherwise none — the user has to consciously pick a
  // destination (e.g. when sharing from a group stream they can't post to,
  // #4818).
  const defaultTargetStream =
    stream &&
    (stream.slug === "all"
      ? firstWritableNetworkStream
      : isWritable(stream)
        ? stream
        : undefined);

  useEffect(() => {
    // No derivable target → write nothing (especially not []): the form
    // default and pre-populated selections (e.g. the ?fileId share flow in
    // ActivityComposer) stay intact.
    if (hasPresetTargeting || !defaultTargetStream) return;
    setValue("activitystreams", [
      { label: defaultTargetStream.name, value: defaultTargetStream.slug },
    ]);
    // Deliberately keyed on slugs instead of object identities: the default is
    // re-applied when the current stream or the derived target actually
    // changes, but never on mere re-renders — a manually cleared selection
    // must not be refilled (#4660). hasPresetTargeting is read as a guard but
    // is no dep: a flip alone must never (re)apply the default into a possibly
    // just-reset() form. ActivityComposer batches the share-cancel clear with
    // the close so the flip can't happen while mounted — this is defense in
    // depth. Assumes the activitystreams query data is immutable per session
    // (staleTime: Infinity, never invalidated).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream?.slug, defaultTargetStream?.slug, setValue]);

  const groupedStreams = map(
    groupBy(
      writeableStreams,
      (stream) =>
        stream.category_name ||
        I18n.t(
          "js.activitystreams.composer.stream_targeting.selection.network_streams",
        ),
    ),
    (options, key) => {
      const serializedOptions = map(options, (opt) => ({
        label: opt.name,
        value: opt.slug,
      }));
      return {
        label: key,
        options: orderBy(
          serializedOptions,
          (stream) => toLower(stream.label),
          "asc",
        ),
      };
    },
  );

  return (
    <div className="mb-2">
      <Label label={I18n.t("composer.editor.stream_targeting.label")} />
      <Controller
        name="activitystreams"
        control={control}
        rules={{ required: I18n.t("js.activity.validation.select_stream") }}
        render={({ field }) => (
          <ReactSelect
            options={groupedStreams}
            placeholder={I18n.t("composer.editor.stream_targeting.label")}
            noOptionsMessage={() => I18n.t("js.plugins.select2.no_match")}
            className={classNames("form-select-container", {
              "field-error field-error-live": errors.activitystreams,
            })}
            classNamePrefix="form-select"
            closeMenuOnSelect
            unstyled
            isMulti
            isSearchable
            isLoading={isLoading}
            {...field}
          />
        )}
      />
      {hint && <Hint text={hint} />}
    </div>
  );
}
